import { useUserPreferencesStore } from "@/app/stores/user-preferences.store.ts";
import { resize } from "@/app/utils/resize.ts";
import {
  enforceMinimumSize,
  ignoreBelowMinimum,
  pxToPdfPoints,
  scaleUpToMinimumSize,
} from "@/app/utils/signature-size.ts";
import {
  type PdfAnnotationObjectBase,
  PdfAnnotationSubtype,
  type PdfPageObject,
  type Rect,
  restoreOffset,
  restoreRect,
  type Size,
  transformSize,
} from "@embedpdf/models";
import { type AnnotationScope } from "@embedpdf/plugin-annotation/vue";
import type { useActiveDocument } from "@embedpdf/plugin-document-manager/vue";
import { type ScrollScope } from "@embedpdf/plugin-scroll";
import { debounce, isNil, orderBy } from "lodash";
import { type Ref } from "vue";
import { getPageRotation } from "./page-rotation.util.ts";

export const ANNOT_AUTHOR_NAME = "Le Parapheur";

/* Data describing the rectangle, based on a coordinate system where the origin
 * is in the lower left corner of the document page */
export type SignCoordinates = {
  upperLeftPoint: { x: number; y: number };
  width: number;
  height: number;
  pageIndex: number;
};

export type UpdateSignCoordinates = Pick<SignCoordinates, "upperLeftPoint" | "width" | "height">;

export abstract class AbstractSignObject {
  abstract idStr: string;
  abstract readonly name: string;

  public coordinates: SignCoordinates;
  private userPreferencesStore: ReturnType<typeof useUserPreferencesStore>;

  protected constructor(
    /* There is a weird bug with annotationApi from embedpdf
     * we need a closure here or it won't always work as intended */
    protected annotationApi: () => Ref<AnnotationScope | null>,
    private scrollProvide: () => Ref<ScrollScope | null>,
    private activeDocument: () => ReturnType<typeof useActiveDocument>,
    initialCoordinates?: SignCoordinates,
    requiredSize?: Size
  ) {
    this.userPreferencesStore = useUserPreferencesStore();
    this.coordinates = initialCoordinates
      ? { ...initialCoordinates, ...enforceMinimumSize(initialCoordinates) }
      : this.getDefaultCoordinates(requiredSize);
  }

  get domId() {
    return "sign-object-" + this.idStr;
  }

  private get annotation() {
    return this.annotationApi().value?.getAnnotationById(this.idStr);
  }

  // x/y are a screen-space (display) delta - e.g. from an arrow key press -
  // convert to raw space before applying, or the move direction is wrong on a
  // rotated page.
  moveRelative({ x = 0, y = 0 }: { x?: number; y?: number }) {
    const page = this.getDocumentPage(this.coordinates.pageIndex);
    const rotation = getPageRotation(this.coordinates.pageIndex, this.activeDocument().activeDocument.value);
    const delta = restoreOffset({ x, y }, rotation, 1);
    const currentRect = this.annotation!.object.rect;
    const maxX = page.size.width - currentRect.size.width;
    const maxY = page.size.height - currentRect.size.height;
    this.annotationApi().value?.updateAnnotation(this.coordinates.pageIndex, this.idStr, {
      rect: {
        origin: {
          x: Math.max(0, Math.min(maxX, currentRect.origin.x + delta.x)),
          y: Math.max(0, Math.min(maxY, currentRect.origin.y + delta.y)),
        },
        size: currentRect.size,
      },
    });
  }

  // width/height are display-space growth - on a rotated page (90/270), the
  // page's raw width/height axes are swapped relative to the screen.
  resizeRelative({ width, height }: { width?: number; height?: number }) {
    const rotation = getPageRotation(this.coordinates.pageIndex, this.activeDocument().activeDocument.value);
    const swapped = rotation % 2 === 1;
    const widthDelta = swapped ? height : width;
    const heightDelta = swapped ? width : height;
    const currentRect = this.annotation!.object.rect;
    if (!isNil(widthDelta)) {
      this.annotationApi().value?.updateAnnotation(this.coordinates.pageIndex, this.idStr, {
        rect: {
          origin: currentRect.origin,
          size: {
            width: Math.max(10, currentRect.size.width + widthDelta),
            height: currentRect.size.height,
          },
        },
      });
    }
    if (!isNil(heightDelta)) {
      this.annotationApi().value?.updateAnnotation(this.coordinates.pageIndex, this.idStr, {
        rect: {
          origin: currentRect.origin,
          size: {
            width: currentRect.size.width,
            height: Math.max(10, currentRect.size.height + heightDelta),
          },
        },
      });
    }
  }

  async delete() {
    this.annotationApi().value?.deleteAnnotation(this.coordinates.pageIndex, this.idStr);
  }

  select({ scrollTo: scrollTo }: { scrollTo: boolean } = { scrollTo: true }) {
    this.annotationApi().value?.selectAnnotation(this.coordinates.pageIndex, this.idStr);
    if (scrollTo) {
      this.scrollTo();
    }
  }

  scrollTo() {
    if (!this.scrollProvide().value) {
      return;
    }

    const { visibleHeight, visibleWidth } = this.scrollProvide()
      .value!.getMetrics()
      .pageVisibilityMetrics.reduce(
        (acc, curr) => {
          acc.visibleHeight = acc.visibleHeight + curr.original.visibleHeight;
          acc.visibleWidth = acc.visibleWidth + curr.original.visibleWidth;
          return acc;
        },
        { visibleHeight: 0, visibleWidth: 0 }
      );
    const { origin, size } = this.annotation!.object.rect;
    const center = { x: origin.x + size.width / 2, y: origin.y + size.height / 2 };

    this.scrollProvide().value!.scrollToPage({
      pageNumber: this.coordinates.pageIndex + 1,
      pageCoordinates: {
        x: center.x - visibleWidth / 2,
        y: center.y - visibleHeight / 2,
      },
      behavior: "smooth",
    });
  }

  deselect() {
    this.annotationApi().value?.deselectAnnotation();
  }

  update = debounce(
    async ({ origin, size }: Rect) => {
      const page = this.getDocumentPage(this.coordinates.pageIndex);

      /* embedpdf uses a coordinate system where the origin is in the top-left corner
       *  instead of the PDF standard of having the origin in the lower-left corner */
      const upperLeftPoint = { x: origin.x, y: page.size.height - origin.y };
      const updatedCoordinates = { upperLeftPoint, ...enforceMinimumSize(size) };
      this.restoreAnnotationSize(origin, size, updatedCoordinates);

      await this.onUpdate?.(updatedCoordinates);
      this.coordinates = { ...updatedCoordinates, pageIndex: this.coordinates.pageIndex };
    },
    500,
    { trailing: true }
  );

  protected onUpdate?(updatedCoordinates: UpdateSignCoordinates): void | Promise<void>;

  protected getBaseAnnotation(): PdfAnnotationObjectBase {
    const page = this.getDocumentPage(this.coordinates.pageIndex);
    const pageHeight = page.size.height;

    /* embedpdf uses a coordinate system where the origin is in the top-left corner
     *  instead of the PDF standard of having the origin in the lower-left corner */
    return {
      type: PdfAnnotationSubtype.UNKNOWN,
      author: ANNOT_AUTHOR_NAME,
      pageIndex: this.coordinates.pageIndex,
      id: this.idStr,
      rect: {
        origin: {
          x: this.coordinates.upperLeftPoint.x,
          y: pageHeight - this.coordinates.upperLeftPoint.y,
        },
        size: {
          width: this.coordinates.width,
          height: this.coordinates.height,
        },
      },
    };
  }

  private getDefaultCoordinates(requiredSize?: Size): SignCoordinates {
    const mostVisiblePage = orderBy(
      this.scrollProvide().value!.getMetrics().pageVisibilityMetrics,
      ["visiblePercentage", "pageNumber"],
      ["desc", "asc"]
    )[0];

    const pageIndex = mostVisiblePage.pageNumber - 1;
    const page = this.getDocumentPage(pageIndex);
    const rotation = getPageRotation(pageIndex, this.activeDocument().activeDocument.value);

    // mostVisiblePage.original.* is in display (rotated) space, but annotation
    // rects are stored raw - compute the box in display space, where centering
    // makes sense, then convert back.
    const displaySize = transformSize(page.size, rotation, 1);
    const { width: displayWidth, height: displayHeight } = scaleUpToMinimumSize(
      this.calculateDefaultSize(displaySize, requiredSize)
    );
    const displayRect: Rect = {
      origin: {
        x: mostVisiblePage.original.pageX + (mostVisiblePage.original.visibleWidth - displayWidth) / 2,
        y: mostVisiblePage.original.pageY + (mostVisiblePage.original.visibleHeight - displayHeight) / 2,
      },
      size: { width: displayWidth, height: displayHeight },
    };
    const { origin, size } = restoreRect(displaySize, displayRect, rotation, 1);

    /* embedpdf uses a coordinate system where the origin is in the top-left corner
     *  instead of the PDF standard of having the origin in the lower-left corner */
    return {
      pageIndex: pageIndex,
      upperLeftPoint: {
        x: origin.x,
        y: page.size.height - origin.y,
      },
      width: size.width,
      height: size.height,
    };
  }

  private calculateDefaultSize(page: Size, requiredSize?: Size): { width: number; height: number } {
    if (requiredSize && requiredSize.width > 0 && requiredSize.height > 0) {
      const size = {
        width: pxToPdfPoints(requiredSize.width)!,
        height: pxToPdfPoints(requiredSize.height)!,
      };
      const fitsPage = size.width <= page.width && size.height <= page.height;
      return fitsPage ? size : resize(page, size.height / size.width);
    }

    const preferredWidth = ignoreBelowMinimum(pxToPdfPoints(this.userPreferencesStore.prepoWidth));
    const preferredHeight = ignoreBelowMinimum(pxToPdfPoints(this.userPreferencesStore.prepoHeight));

    const width = preferredWidth && Math.min(page.width, preferredWidth);
    const height = preferredHeight && Math.min(page.height, preferredHeight);
    const defaultRatio = 0.7;
    if (width || height) {
      return {
        width: width || height! / defaultRatio,
        height: height || width! * defaultRatio,
      };
    }

    const defaultProportion = 0.1;
    const defaultHeight =
      page.height > page.width ? page.height * defaultProportion : page.width * defaultProportion * defaultRatio;
    const defaultWidth = defaultHeight / defaultRatio;
    return resize({ height: defaultHeight, width: defaultWidth }, defaultRatio);
  }

  // the annotation must not stay smaller than the size that gets stored and signed
  private restoreAnnotationSize(origin: Rect["origin"], size: Size, stored: Size) {
    if (stored.width === size.width && stored.height === size.height) {
      return;
    }
    this.annotationApi().value?.updateAnnotation(this.coordinates.pageIndex, this.idStr, {
      rect: { origin, size: { width: stored.width, height: stored.height } },
    });
  }

  private getDocumentPage(pageIndex: number): PdfPageObject {
    return this.activeDocument().activeDocument.value!.document!.pages.find(({ index }) => index === pageIndex)!;
  }

  static sort<T extends AbstractSignObject>(list: T[]): T[] {
    return orderBy(
      list,
      ["coordinates.pageIndex", "coordinates.upperLeftPoint.y", "coordinates.upperLeftPoint.x"],
      ["asc", "desc", "asc"]
    );
  }
}
